import { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        alert("Der skete en fejl. Prøv venligst igen.");
      }
    } catch (error) {
      alert("Der skete en fejl. Prøv venligst igen.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="section-padding min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md px-4">
          <div className="w-20 h-20 bg-green-100 text-green-600 flex items-center justify-center mx-auto">
            <CheckCircle2 size={40} />
          </div>
          <h1 className="text-3xl font-semibold">Tak for din besked!</h1>
          <p className="text-slate-600">
            Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.
          </p>
          <button 
            onClick={() => setSubmitted(false)}
            className="text-slate-900 font-medium hover:underline"
          >
            Send en ny besked
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-slate-50 overflow-hidden">
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tighter text-slate-900 mb-6">
              Lad os tage en <span className="text-blue-600">snak</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Har du spørgsmål til vores produkter, eller har du en idé til en specialopgave? 
              Vi sidder klar til at hjælpe dig med at realisere dit projekt.
            </p>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-1/3 h-full bg-slate-100 -skew-x-12 transform translate-x-20 hidden lg:block"></div>
      </section>

      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Info */}
            <div className="space-y-8">
              <div className="space-y-6">
                <h2 className="text-3xl font-semibold text-slate-900">Kontaktinformation</h2>
                
                <div className="flex items-center gap-4 p-6 bg-slate-50 border border-slate-100 shadow-sm">
                  <div className="w-12 h-12 bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                    <Phone size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Ring til os</p>
                    <div className="flex flex-wrap items-center gap-x-2 text-lg font-semibold text-slate-900">
                      <a href="tel:+4581980952" className="hover:text-blue-600 transition-colors">+45 81 98 09 52</a>
                      <span className="text-slate-400">/</span>
                      <a href="tel:+4581951952" className="hover:text-blue-600 transition-colors">+45 81 95 19 52</a>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-6 bg-slate-50 border border-slate-100 shadow-sm">
                  <div className="w-12 h-12 bg-orange-100 text-orange-600 flex items-center justify-center shrink-0">
                    <Mail size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Send en mail</p>
                    <a href="mailto:hj3ddesign@outlook.dk" className="text-lg font-semibold hover:text-blue-600 transition-colors">hj3ddesign@outlook.dk</a>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-6 bg-slate-50 border border-slate-100 shadow-sm">
                  <div className="w-12 h-12 bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Besøg os</p>
                    <p className="text-lg font-semibold">Jagtvej 82, 6705 Esbjerg</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-6 bg-slate-50 border border-slate-100 shadow-sm">
                  <div className="w-12 h-12 bg-slate-100 text-slate-700 flex items-center justify-center shrink-0 font-bold text-sm">
                    CVR
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Virksomhedsinformation</p>
                    <p className="text-lg font-semibold">CVR: 46368274</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white p-8 md:p-12 shadow-xl border border-slate-100">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot */}
                <div className="sr-only absolute" aria-hidden="true">
                  <input
                    type="text"
                    name="website_url"
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Navn</label>
                    <input
                      required
                      name="name"
                      type="text"
                      placeholder="Dit navn"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Email</label>
                    <input
                      required
                      name="email"
                      type="email"
                      placeholder="din@mail.dk"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Emne</label>
                  <select
                    name="subject"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  >
                    <option>Spørgsmål til produkt</option>
                    <option>Specialopgave</option>
                    <option>Erhvervshenvendelse</option>
                    <option>Andet</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Besked</label>
                  <textarea
                    required
                    name="message"
                    rows="5"
                    placeholder="Hvordan kan vi hjælpe dig?"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary justify-center py-4 text-lg"
                >
                  {loading ? "Sender..." : (
                    <>
                      Send Besked <Send size={20} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
